
import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { PieChart, ChevronDown, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatCurrency, formatDate } from "./utils/costCalculations";

interface TaskProfitability {
  sn: number;
  taskName: string;
  startDate: string;
  endDate: string;
  duration: number;
  numResources: number;
  totalResourceCost: number;
  withBuffer: number;
  profitMargin: number;
  priceToCustomer: number;
  blendedRateToCustomer: number;
}

interface TaskProfitabilityTableProps {
  taskProfitability: TaskProfitability[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onDownload: () => void;
}

const TaskProfitabilityTable = ({ 
  taskProfitability, 
  isOpen, 
  onOpenChange, 
  onDownload 
}: TaskProfitabilityTableProps) => {
  if (taskProfitability.length === 0) return null;

  return (
    <Collapsible open={isOpen} onOpenChange={onOpenChange}>
      <Card className="bg-secondary/50 backdrop-blur-lg border-primary">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CollapsibleTrigger className="flex-1">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  Detailed Projections By Project Task
                </CardTitle>
                
                <div className="flex items-center gap-2">
                  <span className="text-xs text-primary-light">(Click to expand or collapse)</span>
                  <ChevronDown className={`h-4 w-4 shrink-0 transition-transform duration-200 text-primary-light ${isOpen ? 'rotate-180' : ''}`} />
                </div>
              </div>
            </CollapsibleTrigger>
            <Button
              onClick={onDownload}
              className="ml-4 bg-primary hover:bg-primary/90 text-black"
              size="sm"
            >
              Download
            </Button>
          </div>
        </CardHeader>
        <CollapsibleContent>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-primary-light text-[10px]">SN</TableHead>
                  <TableHead className="text-primary-light text-[10px]">Project Task</TableHead>
                  <TableHead className="text-primary-light text-[10px]">Start Date</TableHead>
                  <TableHead className="text-primary-light text-[10px]">End Date</TableHead>
                  <TableHead className="text-primary-light text-[10px]">Duration</TableHead>
                  <TableHead className="text-primary-light text-[10px]"># Resources</TableHead>
                  <TableHead className="text-primary-light text-[10px]">Total Resource Cost</TableHead>
                  <TableHead className="text-primary-light text-[10px]">Total Resource Cost (With Buffer)</TableHead>
                  <TableHead className="text-primary-light text-[10px]">Profit Margin</TableHead>
                  <TableHead className="text-primary-light text-[10px]">Projected Price to Customer</TableHead>
                  <TableHead className="text-primary-light text-[10px]">Blended Rate to Customer</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {taskProfitability.map((task) => (
                  <TableRow key={task.sn}>
                    <TableCell className="text-white text-[10px]">{task.sn}</TableCell>
                    <TableCell className="text-white text-[10px]">{task.taskName}</TableCell>
                    <TableCell className="text-white text-[10px]">{formatDate(task.startDate)}</TableCell>
                    <TableCell className="text-white text-[10px]">{formatDate(task.endDate)}</TableCell>
                    <TableCell className="text-white text-[10px]">{task.duration} days</TableCell>
                    <TableCell className="text-white text-[10px]">{task.numResources}</TableCell>
                    <TableCell className="text-white text-[10px]">{formatCurrency(task.totalResourceCost)}</TableCell>
                    <TableCell className="text-white text-[10px]">{formatCurrency(task.withBuffer)}</TableCell>
                    <TableCell className="text-white text-[10px]">{task.profitMargin}%</TableCell>
                    <TableCell className="text-white text-[10px]">{formatCurrency(task.priceToCustomer)}</TableCell>
                    <TableCell className="text-white text-[10px]">{formatCurrency(task.blendedRateToCustomer)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export default TaskProfitabilityTable;
